import React, { useMemo } from 'react';

import Rect from '@lyearn/core/components/Rect';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import Loader from '@/components/Loader';
import { ModalVariant } from '@/components/Modals/types';
import Table from '@/components/Table';
import { commonTableStyles } from '@/components/Table/styles';
import { UserFragment } from '@/graphql/fragments/generated/User';
import { tableStyles } from '@/modules/corporate/components/LearnerAndGroupDialog/styles';
import { TRANSLATIONS } from '@/modules/corporate/components/LearnerAndGroupDialog/utils';
import AdminEmptyState from '@/modules/corporate/modules/admin/components/EmptyState';

import { UserCollectionFragment } from '../../modules/group/views/GroupManager/graphql/fragments/generated/UserCollection';
import useSeeAllLearnerAndGroups from './hooks/useSeeAllLearnerAndGroups';
import { modalStyles } from './styles';
import { getTableColumns } from './utils';

type SeeAllLearnerAndGroupProps = {
  onClose: () => void;
  learnerIds: string[];
  userGroupIds: string[];
  disabled?: boolean;
  onRemoveUser: ({ removedUserId, isInGroups }: any) => void;
  onRemovegroup: ({ removedGroupId }: any) => void;
  importedUserGroupIds?: string[];
  removedUserIds?: string[];
};

const MODAL_CLASSES = {
  paper: cn(modalStyles, 'h-full'),
};

function SeeAllLearnerAndGroup(props: SeeAllLearnerAndGroupProps) {
  const { t } = useTranslation();
  const {
    onClose,
    learnerIds,
    userGroupIds,
    onRemovegroup,
    onRemoveUser,
    disabled,
    importedUserGroupIds,
    removedUserIds,
  } = props;
  const {
    data: {
      data,
      filterManager: {
        state: { sort, filter, search },
        methods: { setSort, setFilter, setSearch, clearSearch, clearFilters },
      },
      isFetching,
      sortAndFilterItems,
      totalCount,
      fetchMore,
    },
    tabs,
    inViewTab,
  } = useSeeAllLearnerAndGroups({
    learnerIds,
    userGroupIds,
    removedUserIds,
    importedUserGroupIds,
  });

  const tableColumns = useMemo(
    () =>
      getTableColumns({
        inViewTab,
        onRemovegroup,
        onRemoveUser,
        disabled,
      }),
    [disabled, inViewTab, onRemoveUser, onRemovegroup],
  );

  const renderAdaptedContents = () => {
    if (data?.length) {
      return (
        <Rect>
          {({ width, height }) => (
            <Table
              className={cn(tableStyles, commonTableStyles)}
              columns={tableColumns}
              data={data}
              headerHeight={0}
              height={height}
              ignoreFunctionInColumnCompare={false}
              rowClassName="flex-1 w-full px-18"
              rowHeight={83}
              rowKey="id"
              width={width}
              onEndReached={fetchMore}
              onEndReachedThreshold={1000}
            />
          )}
        </Rect>
      );
    }

    if (isFetching) {
      return <Loader />;
    }

    return (
      <AdminEmptyState
        className="pb-96"
        clearFilters={clearFilters}
        clearSearch={clearSearch}
        description={t(TRANSLATIONS[inViewTab].description, {
          searchString: search,
        })}
        filter={filter}
        search={search}
        showIllustrationIcon={false}
        title={t(TRANSLATIONS[inViewTab].title)}
      />
    );
  };

  return (
    <ContentListModal.Main<UserFragment | UserCollectionFragment>
      classes={MODAL_CLASSES}
      fetchMore={fetchMore}
      header={{
        title: t('Admin:LearnerAndGroups.AllAssignees'),
        description: disabled
          ? t('Admin:LearnerAndGroups.AllAssigneesDescEdit')
          : t('Admin:LearnerAndGroups.AllAssigneesDesc'),
      }}
      items={data}
      searchBar={{
        value: search,
        onChange: setSearch,
        placeholder: t('Common:Search'),
      }}
      sortComponent={
        sortAndFilterItems ? (
          <SortAndFilter
            className="mt-20 mb-12 px-8"
            filter={filter}
            setFilter={setFilter}
            setSort={setSort}
            sort={sort}
            sortAndFilterItems={sortAndFilterItems}
          />
        ) : undefined
      }
      tabs={tabs}
      variant={ModalVariant.FullHeight}
      onClose={onClose}>
      <Stack className="h-full w-full flex-1">{renderAdaptedContents()}</Stack>
    </ContentListModal.Main>
  );
}

export default SeeAllLearnerAndGroup;
